import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { useEffect, useRef } from "react";
import { Award, Globe2, Sparkles } from "lucide-react";

const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_tour-innovation-hub/artifacts/090xkr0f_Tony.png";

function Counter({ to, suffix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => {
    const v = Math.round(latest);
    return v.toLocaleString("es-MX");
  });

  useEffect(() => {
    if (inView) {
      const controls = animate(count, to, { duration, ease: "easeOut" });
      return controls.stop;
    }
  }, [inView, to, duration, count]);

  return (
    <span ref={ref} className="inline-flex items-baseline">
      <motion.span>{rounded}</motion.span>
      <span>{suffix}</span>
    </span>
  );
}

const milestones = [
  {
    icon: Sparkles,
    year: "2003",
    title: "Turespacio",
    desc: "Funda la primera plataforma digital de turismo en México, premiada como la mejor web turística del país.",
  },
  {
    icon: Award,
    year: "2011",
    title: "Incubatour",
    desc: "Cofunda Incubatour, ecosistema de emprendimiento turístico galardonado por su innovación y diversificación.",
    highlight: true,
  },
  {
    icon: Globe2,
    year: "Hoy",
    title: "Alcance internacional",
    desc: "Ha trabajado en 24+ países, con operación constante en México, Colombia y España para destinos, hoteles y gobiernos.",
  },
];

export default function SobreTony() {
  return (
    <section
      id="sobre"
      data-testid="about-section"
      className="relative bg-[#070707] py-28 md:py-40 border-t border-white/10 noise overflow-hidden"
    >
      {/* Subtle accent glow */}
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(228,0,124,0.08), transparent 65%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Single editorial portrait */}
          <div className="md:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9 }}
              className="relative"
            >
              {/* Accent corner */}
              <div
                aria-hidden
                className="absolute -top-3 -left-3 w-24 h-24 border-2 border-accent z-0"
              />
              <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/15 bg-[#0a0a0a]">
                <img
                  src={PORTRAIT}
                  alt="Retrato de Tony Yarto"
                  data-testid="tony-portrait"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 font-mono text-[10px] tracking-widest text-white bg-black/55 px-2 py-1">
                  Tony Yarto · 2026
                </div>
              </div>

              {/* Editorial caption */}
              <div className="mt-5 flex items-start gap-3 max-w-xs">
                <span className="font-mono text-[10px] tracking-widest text-accent shrink-0 mt-1">
                  /Retrato
                </span>
                <p className="text-xs text-white/55 leading-relaxed italic">
                  "Cualquier reto se puede convertir en una oportunidad,
                  siempre que tengas la pasión y un poco de locura con método."
                </p>
              </div>
            </motion.div>

            <div className="hidden md:block absolute -left-10 top-4 font-mono text-[10px] tracking-widest text-white/40 rotate-180 [writing-mode:vertical-rl]">
              · Founder · Speaker · Provocador
            </div>
          </div>

          {/* Bio editorial */}
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white/55">
              <span className="h-px w-8 bg-accent" />
              <span className="text-accent">Sobre Tony</span>
              <span className="font-mono text-white/30">/ 02</span>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95]"
            >
              Un provocador
              <br />
              de almas{" "}
              <span className="text-white/25 italic font-light">inquietas</span>
              .
            </motion.h2>

            {/* Highlight badge: Incubatour cofounder */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              data-testid="incubatour-cofounder-badge"
              className="mt-8 inline-flex items-center gap-3 border border-accent/40 bg-accent-soft px-4 py-2.5"
            >
              <span className="h-1.5 w-1.5 bg-accent rounded-full animate-pulse" />
              <span className="text-[11px] uppercase tracking-[0.25em] text-white">
                Socio fundador de{" "}
                <strong className="text-accent">Incubatour</strong>
              </span>
            </motion.div>

            <div className="mt-8 space-y-5 text-white/75 leading-relaxed text-base md:text-lg max-w-2xl">
              <p>
                Más que un consultor en turismo: creador de{" "}
                <strong className="text-white">"Locura con Método"</strong> y
                provocador de ideas audaces. Con más de{" "}
                <strong className="text-white">25 años</strong> en la industria,
                ha demostrado cómo la creatividad y la estrategia pueden
                transformar destinos y empresas.
              </p>
              <p>
                Cofundador de{" "}
                <span className="accent-underline text-white font-medium">
                  Incubatour
                </span>{" "}
                — referente del turismo iberoamericano — y de{" "}
                <strong className="text-white">Turespacio</strong>, primera
                plataforma digital de turismo en México. Hoy opera entre{" "}
                <strong className="text-white">
                  México, Colombia y España
                </strong>{" "}
                impactando a más de 5,000 productos turísticos.
              </p>
            </div>

            {/* Mini timeline */}
            <div className="mt-12 grid md:grid-cols-3 gap-4">
              {milestones.map((m, i) => {
                const Icon = m.icon;
                return (
                  <motion.div
                    key={m.year}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    data-testid={`milestone-${i}`}
                    className={`relative p-5 border ${
                      m.highlight
                        ? "border-accent/50 bg-accent-soft"
                        : "border-white/10 bg-[#0a0a0a]"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon
                        size={16}
                        className={m.highlight ? "text-accent" : "text-white/70"}
                      />
                      <span className="font-mono text-[10px] tracking-widest text-white/40">
                        {m.year}
                      </span>
                    </div>
                    <div className="font-display text-lg font-black uppercase tracking-tight">
                      {m.title}
                    </div>
                    <p className="mt-2 text-xs text-white/60 leading-relaxed">
                      {m.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Counters */}
            <div className="mt-10 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              <div className="bg-[#0a0a0a] p-5 md:p-6" data-testid="stat-years">
                <div className="font-display text-3xl md:text-4xl font-black tracking-tighter">
                  <Counter to={25} suffix="+" />
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Años · Industria
                </div>
              </div>
              <div
                className="bg-[#0a0a0a] p-5 md:p-6"
                data-testid="stat-products"
              >
                <div className="font-display text-3xl md:text-4xl font-black tracking-tighter text-accent">
                  <Counter to={5000} suffix="+" />
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Productos turísticos
                </div>
              </div>
              <div
                className="bg-[#0a0a0a] p-5 md:p-6"
                data-testid="stat-countries"
              >
                <div className="font-display text-3xl md:text-4xl font-black tracking-tighter">
                  <Counter to={24} suffix="+" />
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Países · alcanzados
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
